// src/components/ReadjudicacionMasiva/Documentos/DocumentoViewer.js

import React, { useRef } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    Print as PrintIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            maxWidth: '900px',
            width: '100%',
            height: '90vh',
            margin: theme.spacing(2)
        }
    },
    dialogTitle: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    titleText: {
        fontWeight: 600,
        fontSize: '1rem'
    },
    actionButtons: {
        display: 'flex',
        gap: theme.spacing(1)
    },
    iconButton: {
        color: theme.palette.common.white,
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
    },
    dialogContent: {
        padding: 0,
        overflow: 'auto',
        backgroundColor: theme.palette.grey[100]
    },
    documentContainer: {
        backgroundColor: theme.palette.common.white,
        margin: theme.spacing(2),
        padding: theme.spacing(4),
        minHeight: 'calc(90vh - 120px)',
        boxShadow: theme.shadows[2],
        '& img': {
            maxWidth: '100%',
            height: 'auto'
        },
        '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            '& td, & th': {
                border: '1px solid #ddd',
                padding: '8px'
            }
        }
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
    }
}));

const DocumentoViewer = ({
    open,
    onClose,
    titulo,
    contenido,
    nombreArchivo = 'documento',
    loading = false,
    onDescargarPdf
}) => {
    const classes = useStyles();
    const contentRef = useRef(null);
    const printFrameRef = useRef(null);
    const [generandoPdf, setGenerandoPdf] = React.useState(false);

    const handlePrint = () => {
        // Crear iframe oculto para imprimir sin abrir nueva ventana
        let printFrame = printFrameRef.current;

        if (!printFrame) {
            printFrame = document.createElement('iframe');
            printFrame.style.position = 'absolute';
            printFrame.style.top = '-10000px';
            printFrame.style.left = '-10000px';
            printFrame.style.width = '0';
            printFrame.style.height = '0';
            printFrame.style.border = 'none';
            document.body.appendChild(printFrame);
            printFrameRef.current = printFrame;
        }

        const printDocument = printFrame.contentWindow || printFrame.contentDocument;
        const doc = printDocument.document || printDocument;

        doc.open();
        doc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>${titulo || 'Documento'}</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 20px;
                        margin: 0;
                    }
                    img {
                        max-width: 100%;
                        height: auto;
                    }
                    table {
                        border-collapse: collapse;
                        width: 100%;
                    }
                    table td, table th {
                        border: 1px solid #ddd;
                        padding: 8px;
                    }
                    @media print {
                        body {
                            padding: 0;
                        }
                    }
                </style>
            </head>
            <body>
                ${contenido}
            </body>
            </html>
        `);
        doc.close();

        // Esperar a que el contenido cargue y luego imprimir
        setTimeout(() => {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
        }, 250);
    };

    // Limpiar iframe al desmontar
    React.useEffect(() => {
        return () => {
            if (printFrameRef.current && printFrameRef.current.parentNode) {
                printFrameRef.current.parentNode.removeChild(printFrameRef.current);
            }
        };
    }, []);

    const handleDownloadPdf = async () => {
        if (!onDescargarPdf) return;

        setGenerandoPdf(true);
        try {
            await onDescargarPdf();
        } catch (error) {
            console.error('Error al generar PDF:', error);
        } finally {
            setGenerandoPdf(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth={false}
        >
            <DialogTitle className={classes.dialogTitle}>
                <Typography className={classes.titleText}>
                    {titulo || 'Documento'}
                </Typography>
                <Box className={classes.actionButtons}>
                    <Tooltip title="Imprimir">
                        <IconButton
                            className={classes.iconButton}
                            onClick={handlePrint}
                            disabled={loading || !contenido}
                            size="large">
                            <PrintIcon />
                        </IconButton>
                    </Tooltip>
                    {onDescargarPdf && (
                        <Tooltip title="Descargar PDF">
                            <IconButton
                                className={classes.iconButton}
                                onClick={handleDownloadPdf}
                                disabled={loading || !contenido || generandoPdf}
                                size="large">
                                {generandoPdf ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    <PdfIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Cerrar">
                        <IconButton className={classes.iconButton} onClick={onClose} size="large">
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {loading ? (
                    <Box className={classes.loadingContainer}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <div
                        ref={contentRef}
                        className={classes.documentContainer}
                        dangerouslySetInnerHTML={{ __html: contenido || '' }}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DocumentoViewer;
